import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Send, Save, X } from "lucide-react";

const API = process.env.REACT_APP_API_URL;
const BASE = `${API}/admin/propuestas-comerciales`;

const CommercialProposalsPanel = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [plantilla, setPlantilla] = useState({ asunto: "", titulo: "", cuerpo: "" });
  const [guardandoPlantilla, setGuardandoPlantilla] = useState(false);

  const [pdfs, setPdfs] = useState([]);

  const [clienteNombre, setClienteNombre] = useState("");
  const [contactoNombre, setContactoNombre] = useState("");
  const [emailDestino, setEmailDestino] = useState("");
  const [pdfArchivo, setPdfArchivo] = useState("");
  const [ccoNuevo, setCcoNuevo] = useState("");
  const [cco, setCco] = useState([]);
  const [enviando, setEnviando] = useState(false);

  const [historial, setHistorial] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const cargarPlantilla = () => {
    axios
      .get(`${BASE}/plantilla`, { headers })
      .then((res) =>
        setPlantilla({
          asunto: res.data.asunto,
          titulo: res.data.titulo,
          cuerpo: res.data.cuerpo,
        }),
      )
      .catch(() => toast.error("No se pudo cargar la plantilla"));
  };

  const cargarPdfs = () => {
    axios
      .get(`${BASE}/pdfs`, { headers })
      .then((res) => {
        setPdfs(res.data);
        const preferido = res.data.find((f) => f === "propuesta_enhanced.pdf");
        setPdfArchivo(preferido || res.data[0] || "");
      })
      .catch(() => toast.error("No se pudieron cargar los PDF"));
  };

  const cargarHistorial = (p = 1) => {
    axios
      .get(BASE, { headers, params: { page: p } })
      .then((res) => {
        setHistorial(res.data.data);
        setPage(res.data.page);
        setTotalPages(res.data.totalPages);
      })
      .catch(() => toast.error("No se pudo cargar el historial"));
  };

  useEffect(() => {
    cargarPlantilla();
    cargarPdfs();
    cargarHistorial(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guardarPlantilla = async () => {
    if (!plantilla.asunto.trim() || !plantilla.titulo.trim() || !plantilla.cuerpo.trim()) {
      toast.error("Asunto, título y cuerpo son obligatorios");
      return;
    }
    setGuardandoPlantilla(true);
    try {
      await axios.put(`${BASE}/plantilla`, plantilla, { headers });
      toast.success("Plantilla guardada");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al guardar la plantilla");
    } finally {
      setGuardandoPlantilla(false);
    }
  };

  const agregarCco = () => {
    const correo = ccoNuevo.trim().toLowerCase();
    if (!correo) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      toast.error("Correo inválido");
      return;
    }
    if (cco.includes(correo)) {
      setCcoNuevo("");
      return;
    }
    setCco((prev) => [...prev, correo]);
    setCcoNuevo("");
  };

  const quitarCco = (correo) => setCco((prev) => prev.filter((c) => c !== correo));

  const previsualizar = (texto) =>
    (texto || "")
      .split("{{cliente}}")
      .join(clienteNombre || "{{cliente}}")
      .split("{{contacto}}")
      .join(contactoNombre || "{{contacto}}");

  const enviar = async () => {
    if (!clienteNombre.trim() || !contactoNombre.trim() || !emailDestino.trim()) {
      toast.error("Cliente, contacto y correo de destino son obligatorios");
      return;
    }
    if (!pdfArchivo) {
      toast.error("Selecciona un PDF");
      return;
    }
    setEnviando(true);
    try {
      await axios.post(
        `${BASE}/enviar`,
        { clienteNombre, contactoNombre, emailDestino, cco, pdfArchivo },
        { headers },
      );
      toast.success("Propuesta enviada");
      setClienteNombre("");
      setContactoNombre("");
      setEmailDestino("");
      setCco([]);
      cargarHistorial(1);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al enviar la propuesta");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <h1 className="text-xl font-semibold text-gray-800 mb-6">Propuestas comerciales</h1>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4 mb-8">
        <h2 className="text-lg font-semibold text-gray-800">Plantilla del mensaje</h2>
        <input
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="Asunto"
          value={plantilla.asunto}
          onChange={(e) => setPlantilla((p) => ({ ...p, asunto: e.target.value }))}
        />
        <input
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          placeholder="Título (encabezado del correo)"
          value={plantilla.titulo}
          onChange={(e) => setPlantilla((p) => ({ ...p, titulo: e.target.value }))}
        />
        <textarea
          rows={12}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
          placeholder="Cuerpo del mensaje"
          value={plantilla.cuerpo}
          onChange={(e) => setPlantilla((p) => ({ ...p, cuerpo: e.target.value }))}
        />
        <p className="text-xs text-gray-500">
          Placeholders disponibles: <code>{"{{cliente}}"}</code> y <code>{"{{contacto}}"}</code>
        </p>
        <div className="flex justify-end">
          <button
            onClick={guardarPlantilla}
            disabled={guardandoPlantilla}
            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
          >
            <Save className="h-4 w-4" />
            {guardandoPlantilla ? "Guardando..." : "Guardar plantilla"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4 mb-8">
        <h2 className="text-lg font-semibold text-gray-800">Enviar propuesta</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Nombre del cliente"
            value={clienteNombre}
            onChange={(e) => setClienteNombre(e.target.value)}
          />
          <input
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Nombre de quien recibe"
            value={contactoNombre}
            onChange={(e) => setContactoNombre(e.target.value)}
          />
          <input
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Correo de destino"
            value={emailDestino}
            onChange={(e) => setEmailDestino(e.target.value)}
          />
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={pdfArchivo}
            onChange={(e) => setPdfArchivo(e.target.value)}
          >
            {pdfs.length === 0 && <option value="">Sin PDF disponible</option>}
            {pdfs.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex gap-2">
            <input
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Copia oculta (CCO) — opcional"
              value={ccoNuevo}
              onChange={(e) => setCcoNuevo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  agregarCco();
                }
              }}
            />
            <button
              type="button"
              onClick={agregarCco}
              className="text-sm text-blue-600 border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-50"
            >
              Agregar
            </button>
          </div>
          {cco.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {cco.map((correo) => (
                <span
                  key={correo}
                  className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded-full px-3 py-1"
                >
                  {correo}
                  <button onClick={() => quitarCco(correo)} aria-label="Quitar">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 mb-1">Vista previa del mensaje</p>
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
            <p className="font-semibold mb-2">{previsualizar(plantilla.asunto)}</p>
            {previsualizar(plantilla.cuerpo)}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={enviar}
            disabled={enviando}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
          >
            <Send className="h-4 w-4" />
            {enviando ? "Enviando..." : "Enviar propuesta"}
          </button>
        </div>
      </div>

      <h2 className="text-lg font-semibold text-gray-800 mb-4">Historial</h2>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Fecha</th>
              <th className="text-left px-4 py-2">Cliente</th>
              <th className="text-left px-4 py-2">Contacto</th>
              <th className="text-left px-4 py-2">Correo destino</th>
              <th className="text-left px-4 py-2">PDF</th>
              <th className="text-left px-4 py-2">Estado</th>
              <th className="text-left px-4 py-2">Enviado por</th>
            </tr>
          </thead>
          <tbody>
            {historial.map((r) => (
              <tr key={r._id} className="border-t border-gray-100">
                <td className="px-4 py-2 text-gray-600">
                  {new Date(r.createdAt).toLocaleString("es-CO")}
                </td>
                <td className="px-4 py-2 text-gray-800">{r.clienteNombre}</td>
                <td className="px-4 py-2 text-gray-600">{r.contactoNombre}</td>
                <td className="px-4 py-2 text-gray-600">{r.emailDestino}</td>
                <td className="px-4 py-2 text-gray-600">{r.pdfArchivo}</td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      r.estado === "enviado"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {r.estado}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-600">{r.enviadoPor?.name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => cargarHistorial(p)}
              className={`px-3 py-1 rounded-lg text-sm ${
                p === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default CommercialProposalsPanel;
